import * as React from 'react';
import * as H from 'history';
import * as ContractRepository from '../../Repository/ContractRepository';
import DesktopMenu from '../SiteMenu/DesktopMenu';
import MobileMenu from '../SiteMenu/MobileMenu';
import TreeCounter from './TreeCounter';
import FullPageLoader from '../Loader/FullPageLoader';
import { Alert, Button, Col, Layout, Row } from 'antd';
import { ConnectionState, GlobalContext } from '../../Contexts/GlobalContext';
import { Contract } from 'tree-wallet-client';
import { Navigate } from 'react-router-dom';
import { isAllowed } from '../../Service/AuthService';
import { getImpersonationData, stopImpersonation } from "../../Service/RoleService";
import { UserRoles } from '../../Repository/UserRepository';
import { LoaderContext } from '../../Contexts/LoaderContext';
import { Filter } from '../../Pages/MapPage/MapPage';

const { Header, Content, Footer } = Layout;

export interface PageProps {
  loading?: boolean;
  title?: React.ReactNode;
  history?: H.History;
  hideMenu?: boolean;
  hideCounter?: boolean;
  allowedRoles?: any;
  isPublic?: boolean;
  padded?: boolean;
  /** Drops the content's 16px side gutter, so what it holds reaches both sides of the window.
   *  The map pages ask for it: a map with a white strip down each side does not look like a map. */
  fullWidth?: boolean;
  /** Hands the content every pixel the header leaves and drops the page's own footer, so a page made
   *  of a single full-height element (the public map) reaches the bottom of the window. */
  fullHeight?: boolean;
  treeCount?: number;
  onlineStatus?: ConnectionState;
  trees?: any;
  filter?: Filter;
  children?: React.ReactNode
}

export interface PageState {
  loading: boolean;
}

export default class Page extends React.Component<PageProps, PageState> {
  static contextType = GlobalContext;
  declare context: React.ContextType<typeof GlobalContext>

  constructor(props: PageProps) {
    super(props);
    this.state = {
      loading: true
    }
  }

  async componentDidMount() {
    const { contracts } = this.context;

    // A new page starts at the top: otherwise the previous page's scroll offset is applied to it for a
    // frame, and the banner looks like it jumps into place
    window.scrollTo(0, 0);

    this.setState({ loading: true });
    if (!contracts || !contracts.length) {
      try {
        await ContractRepository.getContractsList({
          successCallback: (response: Contract[]) => {
            this.context.contracts = response;
          }
        });
      } catch (error) {
        // The page still opens: a screen that needs a contract says so itself
        console.error('could not read the contracts of the user', { error });
      }
    }
    return this.setState({ loading: false });
  }

  // The counter is the planter's one-tap switch between the two pages they work in
  private onCounterClick = () => {
    const { navigate, location, contract } = this.context;
    const pathname: string = location?.pathname || window.location.pathname;
    const current = pathname.match(/^\/(\d+)\/(form|list)$/);
    const contractId = current ? current[1] : contract?.id;
    if (!navigate || !contractId) {
      return;
    }
    navigate(`/${contractId}/${current && current[2] === 'list' ? 'form' : 'list'}`);
  };

  render() {
    const { loading, title, hideMenu, hideCounter, allowedRoles, isPublic, padded, fullWidth, fullHeight, filter } = this.props;
    const { treeCount, contract, user , counterRef, impersonating } = this.context;
    // Admin/test pages are rendered without page props: without this, their header never showed (offline)
    const onlineStatus = this.props.onlineStatus ?? this.context.onlineStatus;

    if (loading) {
      return <FullPageLoader />;
    }

    if (!isPublic && !user) {
      return <Navigate to={'/login'} />;
    }

    const isAdmin = user && user?.roles.indexOf(UserRoles.Admin.name) > -1;
    const roles = [...(contract?.roles || []), ...(user?.roles || [])];
    const isUserAllowed = isAllowed(roles, allowedRoles);
    if (
      !isAdmin
      && !isPublic
      && user
      && (!isUserAllowed || !user)
    ) {
      console.info('user not allowed', { user, userRoles: roles, allowedRoles })
      return <Navigate to={'/unauthorized'} />;
    }

    // const role = user && (getRole(user.role)?.label || '');
    const pageTitle = [
      title,
      // user ? voca.capitalize(role) : '',
      onlineStatus === ConnectionState.offline ? '(offline)' : ''
    ].join(' ');

    // The counter is a planter's shortcut: it switches between the two pages they work in
    const isTreePlanter = ContractRepository.hasRole(roles, ContractRepository.roles.TreePlanter.name);

    const impersonationData = impersonating && getImpersonationData()

    // `hasSider` is explicit: antd only adds `ant-layout-has-sider` once the Sider's mount effect ran,
    // so the first painted frame stacked the menu above the header and the banner flickered
    return (
      <Layout hasSider={!hideMenu} style={{ minHeight: '100vh' }}>
        {!hideMenu && (
          <LoaderContext.Consumer>
            {({ setShowLoader }) => (
              <DesktopMenu 
                user={user} 
                contract={contract} 
                setShowLoader={setShowLoader} 
                filter={filter} 
              />
            )}
          </LoaderContext.Consumer>
        )}
        <Layout className="site-layout">
          {impersonating && impersonationData &&
            <Alert
              className='impersonation-strip'
              message={`Impersonating user ${impersonationData['user']}`}
              banner
              closable={false}
              action={
                <Button size="small" type="link" onClick={async () => {
                  await stopImpersonation();
                  window.location.href = '/';
                }}>
                  stop
                </Button>
              }
            />
          }
          <Header className="site-header site-layout-background" style={{ padding: '0' }}>
            <Row className={`site-page-header`}>
              <Col xs={{ span: 5 }} sm={{ span: 5 }} md={{ span: 5 }} className={'header-column'}>
                {!hideMenu && (
                  <LoaderContext.Consumer>
                    {({ setShowLoader }) => (
                      <MobileMenu 
                        user={user} 
                        contract={contract} 
                        setShowLoader={setShowLoader} 
                        filter={filter} 
                      />
                    )}
                  </LoaderContext.Consumer>
                )}
              </Col>
              <Col span={14} className='page-title-container header-column'><div className={'page-title'}>{pageTitle}</div></Col>
              <Col xs={{ span: 5 }} sm={{ span: 5 }} md={{ span: 5 }} className={'header-column tree-counter-container'}>
                {!hideCounter && (
                  <Row justify="end" align="middle" gutter={[8, 0]}>
                    <Col>
                      <TreeCounter key={'counter'} ref={counterRef} treeCount={parseInt(String(treeCount))} onClick={isTreePlanter ? this.onCounterClick : undefined} />
                    </Col>
                  </Row>
                )}
                {hideCounter && (
                  <Row justify="end" align="middle">
                    <Col>
                      {/* <GPSAccuracyIndicator /> */}
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
          </Header>
          <Content style={{ margin: fullWidth ? 0 : '0 16px', padding: !padded ? '' : '20px' }}>
            {this.props.children}
          </Content>
          {/* `fullHeight`: such a page is nothing but a full-height element, and a footer - empty or
              not - would still take its antd padding and leave a strip under it. */}
          {!fullHeight && <Footer style={{ textAlign: 'center' }}/>}
        </Layout>
      </Layout>
    );
  }
}